import { cx } from '@/lib/cx';
import styles from './Named.module.css';

/**
 * A code with its name: `Almaty (ALA)`, `Air Astana (KC)`.
 *
 * In `components/ui` because two screens draw it — the flight board's airline and
 * airport columns, and the international arrivals journal's — and a reader moving between them
 * should not have to learn two conventions for the same idea.
 *
 * The name leads because that is what a reader recognises: most of these codes are
 * places and carriers nobody has memorised. The code stays because it is what the
 * filters take, what the exports write, and what every other screen in this system
 * speaks in.
 *
 * The code ALONE when nothing is known, which is also what shows for the moment
 * before an airport lookup lands. Deliberately not a spinner: the code is already
 * the answer, the name only makes it easier to read, and a row that flickers a
 * placeholder is worse than one that gains a word.
 */
export const Named = ({
  code,
  name,
  showCode = true,
}: {
  code: string | null;
  name: string;
  /**
   * Whether to print the code beside the name.
   *
   * Off where the column's own header already says which code this is, so the cell
   * would be repeating itself. The code still reaches the reader through the title.
   */
  showCode?: boolean;
}) => {
  if (code === null) return <span className={styles.empty}>—</span>;
  if (name === '') return <span className={styles.bare}>{code}</span>;

  return (
    <span className={cx(styles.named)} title={`${name} (${code})`}>
      <span className={styles.label}>{name}</span>
      {showCode && <span className={styles.code}>({code})</span>}
    </span>
  );
};
